import { useEffect, useRef, useState } from "react";
import { CircleAlert, CircleCheck, Loader2 } from "lucide-react";
import Modal from "../ui/Modal";
import { getAiReportErrorMessage, getAiReportSettings, saveAiReportSettings } from "../../api/aiReports";
import { AdminHotelRow } from "../../types/hotels";
import { AI_REPORT_DAY_OPTIONS, normalizeAiReportDays } from "../../utils/aiReports";

type Props = {
  open: boolean;
  hotel: AdminHotelRow | null;
  onClose: () => void;
  onRefresh: () => Promise<void>;
};

export default function AiReportSettingsModal({ open, hotel, onClose, onRefresh }: Props) {
  if (!open) return null;
  return <AiReportSettingsForm key={`${hotel?.id}-${hotel?.group_id}`} hotel={hotel} onClose={onClose} onRefresh={onRefresh} />;
}

function AiReportSettingsForm({ hotel, onClose, onRefresh }: Omit<Props, "open">) {
  const groupId = hotel?.group_id ?? null;
  const [days, setDays] = useState<number[]>(() => normalizeAiReportDays(hotel));
  const [pendingAction, setPendingAction] = useState<"save" | "reset" | null>(null);
  const saving = pendingAction !== null;
  const savingRef = useRef(false);
  const [loading, setLoading] = useState(Boolean(groupId));
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const controllerRef = useRef<AbortController | null>(null);
  const canEdit = Boolean(groupId);

  useEffect(() => {
    if (!groupId) return;
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    getAiReportSettings(groupId, controller.signal)
      .then((settings) => {
        if (!controller.signal.aborted) setDays(settings.days);
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(getAiReportErrorMessage(e, "Не удалось загрузить дни генерации"));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [groupId]);

  function handleClose() {
    if (savingRef.current) return;
    setError("");
    setSuccess("");
    onClose();
  }

  function toggleDay(day: number) {
    setError("");
    setSuccess("");
    setDays((current) =>
      current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day].sort((left, right) => left - right),
    );
  }

  async function submitSettings(nextDays: number[], action: "save" | "reset") {
    if (savingRef.current || loading) return;
    if (!groupId) {
      setError("Для объекта не задана группа парсинга");
      return;
    }

    savingRef.current = true;
    setPendingAction(action);
    setError("");
    setSuccess("");
    const signal = controllerRef.current?.signal;
    try {
      const settings = await saveAiReportSettings({
        group_id: String(groupId),
        days: nextDays,
      }, signal);
      if (signal?.aborted) return;
      setDays(settings.days);
      setSuccess(settings.days.length
        ? "Расписание ИИ-отчётов сохранено"
        : "Расписание сброшено. Автоматическая генерация ИИ-отчётов отключена");
      try {
        await onRefresh();
      } catch {
        if (!signal?.aborted) setError("Изменения сохранены, но не удалось обновить список объектов. Обновите страницу.");
      }
    } catch (e: any) {
      if (!signal?.aborted) setError(getAiReportErrorMessage(e,
        action === "reset" ? "Не удалось сбросить расписание ИИ-отчётов" : "Не удалось сохранить расписание ИИ-отчётов"));
    } finally {
      savingRef.current = false;
      if (!signal?.aborted) setPendingAction(null);
    }
  }

  return (
    <Modal
      open
      onClose={handleClose}
      title={hotel ? `ИИ отчеты: ${hotel.name}` : "ИИ отчеты"}
      widthClass="max-w-2xl"
    >
      <div className="space-y-5">
        {!canEdit && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Для объекта не задана группа парсинга
          </div>
        )}

        <section className="rounded-3xl border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-cyan-50 p-5 shadow-[0_12px_30px_rgba(99,102,241,0.08)]">
          <div>
            <div className="text-sm font-semibold text-slate-900">Частота генерации ИИ отчетов</div>
            <div className="mt-1 text-sm text-slate-500">
              Выберите дни недели, в которые нужно формировать ИИ-отчеты по объекту.
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Кнопка «Сбросить» сразу отключает расписание автоматической генерации.
            </p>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {AI_REPORT_DAY_OPTIONS.map((day) => {
              const selected = days.includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  disabled={!canEdit || saving || loading}
                  aria-pressed={selected}
                  className={`min-w-12 rounded-2xl border px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                    selected
                      ? "border-indigo-500 bg-indigo-600 text-white shadow-[0_10px_20px_rgba(99,102,241,0.22)]"
                      : "border-slate-200 bg-white text-slate-600 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
                  }`}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </section>

        {error && (
          <div role="alert" className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <CircleAlert size={20} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div role="status" className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <CircleCheck size={20} className="shrink-0" />
            <span>{success}</span>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={() => void submitSettings([], "reset")}
            disabled={!canEdit || saving || loading}
            className="mr-auto inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {pendingAction === "reset" && <Loader2 size={16} className="animate-spin" />}
            {pendingAction === "reset" ? "Сбрасываем…" : "Сбросить"}
          </button>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => void submitSettings(days, "save")}
            disabled={!canEdit || saving || loading}
            className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {(pendingAction === "save" || loading) && <Loader2 size={16} className="animate-spin" />}
            {pendingAction === "save" ? "Сохраняем…" : "Сохранить"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
